/**
 * WorkExecutionLauncher · iter303ak-fix29 — Work In Progress · T50 · Action 10
 * ─────────────────────────────────────────────────────────────────────────
 * Deep-links to /appraisals/{parent_id}?embed=job-execution in a new tab.
 * The linked tab hosts the per-child work checklist + Extras Capture +
 * Contractor sign-off. This step polls the parent every few seconds after
 * the tab is opened and re-reads `work_execution_signed_off`; Continue is
 * enabled once it flips to true.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink, PlayCircle, RefreshCw, Wrench } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function WorkExecutionLauncher({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const embedRoute = (params.embed_route || '/appraisals/{parent_id}?embed=job-execution').replace('{parent_id}', parentAppraisalId || '');

  const [signedOff, setSignedOff] = useState(!!capturedData?.appraisal_record?.work_execution_signed_off);
  const [signedOffAt, setSignedOffAt] = useState(capturedData?.appraisal_record?.work_execution_signed_off_at || null);
  const [viewedAt, setViewedAt] = useState(value?.viewed_at || null);
  const [refreshing, setRefreshing] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!parentAppraisalId) return;
    setRefreshing(true);
    try {
      const { data } = await api.get(`/appraisals/${parentAppraisalId}`);
      setSafety({ needed: !!((data?.height_safety_types || []).length || data?.accepted_safety_supplier_id), erected: !!data?.safety_erection?.inspected_at });
      const so = !!data?.work_execution_signed_off;
      setSignedOff(so);
      if (so) setSignedOffAt(data?.work_execution_signed_off_at || new Date().toISOString());
    } catch { /* silent */ } finally { setRefreshing(false); }
  }, [parentAppraisalId]);

  // Poll every 5s while the tab has been opened and we don't yet have signed_off.
  useEffect(() => {
    if (!viewedAt || signedOff || !parentAppraisalId) return;
    const t = setInterval(refreshStatus, 5000);
    return () => clearInterval(t);
  }, [viewedAt, signedOff, parentAppraisalId, refreshStatus]);

  useEffect(() => {
    if (onChange) onChange({ viewed_at: viewedAt, signed_off: signedOff, signed_off_at: signedOffAt });
  }, [viewedAt, signedOff, signedOffAt]);   

  // Safety gate: booking/planning carry on while safety is pending, but work can't START until it's confirmed erected.
  const [safety, setSafety] = useState({ needed: false, erected: true });
  const [override, setOverride] = useState(value?.safety_override_reason || '');
  useEffect(() => { refreshStatus(); }, [refreshStatus]);
  const safetyBlocked = safety.needed && !safety.erected && !override.trim();

  const openTab = () => {
    if (safetyBlocked) return;
    window.open(embedRoute, '_blank', 'noopener,noreferrer');
    if (!viewedAt) setViewedAt(new Date().toISOString());
  };

  return (
    <div className="space-y-4" data-testid="wf-work-execution-launcher">
      {safety.needed && !safety.erected && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="wf-wel-safety-gate">
          <div className="font-semibold">Height safety isn't confirmed as erected yet — work can't start.</div>
          <div className="text-xs">Complete "Confirm Safety Erected" first, or record why work can start anyway.</div>
          <input value={override} onChange={(e) => { setOverride(e.target.value); onChange && onChange({ viewed_at: viewedAt, signed_off: signedOff, signed_off_at: signedOffAt, safety_override_reason: e.target.value }); }}
            data-testid="wf-wel-safety-override" placeholder="Override reason (logged on this run)"
            className="w-full border border-amber-300 rounded px-2 py-1.5 text-sm bg-white" />
        </div>
      )}
      <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4">
        <div className="flex items-start gap-3">
          <Wrench className="w-5 h-5 mt-0.5 shrink-0 text-violet-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-violet-900">Job Execution — Open Work Tab</div>
            <div className="text-sm mt-1 text-violet-800">
              {params.prompt || 'Open the Job Execution tab to capture the work checklist, extras, and contractor sign-off.'}
            </div>
          </div>
        </div>
      </div>

      <button onClick={openTab} disabled={safetyBlocked}
        data-testid="wf-wel-open-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 text-white font-semibold rounded hover:bg-violet-700 transition-colors">
        <PlayCircle size={18} />
        Open Job Execution tab
        <ExternalLink size={14} />
      </button>

      {viewedAt && (
        <div className="text-xs text-slate-500">
          Last opened at <span className="font-mono">{new Date(viewedAt).toLocaleString()}</span>
        </div>
      )}

      <div className={`p-4 border rounded-lg ${signedOff ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={18} className={signedOff ? 'text-emerald-600' : 'text-slate-300'} />
            <span className={`text-sm font-medium ${signedOff ? 'text-emerald-800' : 'text-slate-600'}`}>
              {signedOff
                ? `Contractor signed off — ${signedOffAt ? new Date(signedOffAt).toLocaleString() : ''}`
                : 'Waiting for contractor sign-off (auto-refresh every 5s)'}
            </span>
          </div>
          <button onClick={refreshStatus} disabled={refreshing}
            data-testid="wf-wel-refresh"
            className="px-2.5 py-1 rounded text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!signedOff || safetyBlocked}
            data-testid="wf-wel-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
